import { useRef, useState } from 'react';
import type { Bereich, Datei } from '@beholder/shared';
import { api } from './api';
import { Symbol } from './symbole';
import { Knopf, Meldung, datumZeit, groesse, useAktion, useDaten } from './ui';

/**
 * Anlagen an einem Eintrag (z. B. Mindmap-Knoten): Liste, Hochladen per Knopf oder Ziehen, Löschen.
 * Nutzung: <Anlagen projektId={p.id} bereich="mindmap" bezug={knoten.id} darfSchreiben={…} />
 */
export function Anlagen({
  projektId,
  bereich,
  bezug,
  darfSchreiben,
  beiAenderung,
}: {
  projektId: string;
  bereich: Bereich;
  bezug: string;
  darfSchreiben: boolean;
  beiAenderung?(dateien: Datei[]): void;
}) {
  const pfad = `/api/projekte/${projektId}/dateien?bereich=${bereich}&bezug=${encodeURIComponent(bezug)}`;
  const { daten, neuLaden, fehler } = useDaten(() => api.get<Datei[]>(pfad), [pfad]);
  const aktion = useAktion();
  const eingabe = useRef<HTMLInputElement>(null);
  const [ziehen, setZiehen] = useState(false);

  async function hochladen(dateien: FileList | File[]) {
    if (!dateien.length) return;
    await aktion.ausfuehren(() => api.hochladen<Datei[]>(pfad, dateien));
    const neu = await api.get<Datei[]>(pfad);
    await neuLaden();
    beiAenderung?.(neu);
  }

  async function loeschen(d: Datei) {
    if (!confirm(`„${d.name}“ wirklich löschen?`)) return;
    await aktion.ausfuehren(() => api.delete(`/api/dateien/${d.id}`));
    const neu = await api.get<Datei[]>(pfad);
    await neuLaden();
    beiAenderung?.(neu);
  }

  return (
    <div
      className={`anlagen${ziehen ? ' anlagen-ziehen' : ''}`}
      onDragOver={(e) => {
        if (!darfSchreiben) return;
        e.preventDefault();
        setZiehen(true);
      }}
      onDragLeave={() => setZiehen(false)}
      onDrop={(e) => {
        if (!darfSchreiben) return;
        e.preventDefault();
        setZiehen(false);
        void hochladen(e.dataTransfer.files);
      }}
    >
      <Meldung>{fehler || aktion.fehler}</Meldung>
      {daten && daten.length === 0 && <p className="leise">Keine Anlagen{darfSchreiben ? ' – Dateien hierher ziehen oder hochladen.' : '.'}</p>}
      <ul className="anlagen-liste">
        {daten?.map((d) => (
          <li key={d.id}>
            <span className="anlage-symbol">{Symbol.datei}</span>
            <a href={`/api/dateien/${d.id}`} className="anlage-name" title={d.name}>
              {d.name}
            </a>
            <span className="leise klein">
              {groesse(d.groesse)} · {d.hochgeladenVonName ?? 'unbekannt'} · {datumZeit(d.erstelltAm)}
            </span>
            {darfSchreiben && (
              <button type="button" className="knopf knopf-leise knopf-klein" aria-label={`${d.name} löschen`} onClick={() => loeschen(d)}>
                {Symbol.muell}
              </button>
            )}
          </li>
        ))}
      </ul>
      {darfSchreiben && (
        <>
          <input ref={eingabe} type="file" multiple hidden onChange={(e) => e.target.files && hochladen(e.target.files)} />
          <Knopf klein onClick={() => eingabe.current?.click()} disabled={aktion.laeuft}>
            {Symbol.hochladen} {aktion.laeuft ? 'Lädt hoch …' : 'Datei hochladen'}
          </Knopf>
        </>
      )}
    </div>
  );
}
